/**
 * useFleetLive — the fleet's state PUSHED to the page, no refresh button.
 *
 *   status   WebSocket  /svc/engine-ctrl/api/engine/ws   (Control-Service engine API: a status frame
 *            every few seconds, straight from TCP + /health probes of every service)
 *   traffic  event bus  /svc/event-bus/api/v2/stream?topics=…   (server-sent events: what is actually
 *            being published, by whom)
 *
 * Both reconnect on their own with backoff. While the WebSocket is not connected, status falls back to
 * the shared fleet probe (ServiceHealthContext), and `transport` says which one is in use — the page
 * never shows a state without saying where it came from.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { INFRA_SERVICES, SERVICE_REGISTRY } from '@/config/serviceRegistry';
import { useServiceHealth } from '@/contexts/ServiceHealthContext';
import { setVisibleInterval } from '@/utils/visibleInterval';

/** STANDBY = down by design (the control API's `standby`, e.g. core-brain in the cognitive profile,
 *  where the learning-engine kernel decides). It is not an error and is drawn as such. */
export type LiveStatus = 'UP' | 'DOWN' | 'PROBING' | 'STANDBY';
export type Transport = 'websocket' | 'probe';

export interface TrafficEvent {
  id: string;
  at: number;                 // ms
  topic: string;
  source: string | null;      // publishing service, when the event names it
}

const WS_PATH = '/svc/engine-ctrl/api/engine/ws';
const STREAM_PATH = '/svc/event-bus/api/v2/stream';
const TOPICS = ['world.snapshot.v1', 'decisions', 'brain.decision', 'perception.events', 'candles', 'shape_created',
  'shape_updated', 'spine.ontology.v1', 'spine.knowledge.v1', 'spine.meaning.v1', 'spine.reasoning.v1',
  'policy.evaluated', 'learning.updates', 'simulation.outcomes', 'explanation.ready'];
const STALE_MS = 20000;
const MAX_TRAFFIC = 40;

const BY_PORT = new Map([...SERVICE_REGISTRY, ...INFRA_SERVICES].map(s => [s.port, s.id]));
const KNOWN_IDS = new Set([...SERVICE_REGISTRY, ...INFRA_SERVICES].map(s => s.id));
// The control API names two services differently from the registry.
const ALIAS: Record<string, string> = { auth: 'auth-system', ontology: 'ontology', perception: 'perception', 'core-brain': 'core-brain' };

function idFor(name: string, port: number | undefined): string | null {
  if (port !== undefined && BY_PORT.has(port)) return BY_PORT.get(port) as string;
  const id = ALIAS[name] ?? name;
  return KNOWN_IDS.has(id) ? id : null;
}

/** One service row of a control-API status frame as a board status. */
export function liveStatusOf(svc: { state?: unknown; error?: unknown }): LiveStatus {
  if (svc.state === 'standby') return 'STANDBY';
  return svc.state === 'running' && !svc.error ? 'UP' : 'DOWN';
}

export function useFleetLive() {
  const probe = useServiceHealth();
  const [pushed, setPushed] = useState<Record<string, LiveStatus>>({});
  const [pushedAt, setPushedAt] = useState(0);
  const [socketOpen, setSocketOpen] = useState(false);
  const [busOpen, setBusOpen] = useState(false);
  const [traffic, setTraffic] = useState<TrafficEvent[]>([]);
  const [lastSeen, setLastSeen] = useState<Record<string, number>>({});   // service id -> ms of last published event
  const [, setTick] = useState(0);
  const seq = useRef(0);

  // ── status over WebSocket ───────────────────────────────────────────────────────────────
  useEffect(() => {
    let ws: WebSocket | null = null;
    let timer: number | null = null;
    let closed = false;
    let attempt = 0;

    const connect = () => {
      if (closed) return;
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      try {
        ws = new WebSocket(`${scheme}://${window.location.host}${WS_PATH}`);
      } catch {
        schedule();
        return;
      }
      ws.onopen = () => { attempt = 0; setSocketOpen(true); };
      ws.onmessage = (msg) => {
        try {
          const frame = JSON.parse(msg.data);
          const services = frame?.payload?.services;
          if (!services || typeof services !== 'object') return;
          const next: Record<string, LiveStatus> = {};
          for (const svc of Object.values<any>(services)) {
            const id = idFor(String(svc.name ?? svc.id ?? ''), typeof svc.port === 'number' ? svc.port : undefined);
            if (id) next[id] = liveStatusOf(svc);
          }
          setPushed(next);
          setPushedAt(Date.now());
        } catch { /* a malformed frame is ignored, the next one replaces it */ }
      };
      ws.onclose = () => { setSocketOpen(false); schedule(); };
      ws.onerror = () => { try { ws?.close(); } catch { /* already closing */ } };
    };
    const schedule = () => {
      if (closed) return;
      attempt += 1;
      timer = window.setTimeout(connect, Math.min(15000, 1000 * 2 ** Math.min(attempt, 4)));
    };
    connect();
    return () => {
      closed = true;
      if (timer !== null) window.clearTimeout(timer);
      try { ws?.close(); } catch { /* nothing to close */ }
    };
  }, []);

  // ── traffic from the event bus ──────────────────────────────────────────────────────────
  useEffect(() => {
    let es: EventSource | null = null;
    let timer: number | null = null;
    let closed = false;
    let attempt = 0;

    const connect = () => {
      if (closed) return;
      es = new EventSource(`${STREAM_PATH}?topics=${encodeURIComponent(TOPICS.join(','))}`);
      es.onopen = () => { attempt = 0; setBusOpen(true); };
      es.onmessage = (msg) => {
        try {
          const ev = JSON.parse(msg.data);
          const topic = String(ev.topic ?? ev.event_type ?? 'event');
          const rawSource = ev.source ?? ev.headers?.source_service ?? null;
          const source = rawSource ? (KNOWN_IDS.has(rawSource) ? rawSource : ALIAS[rawSource] ?? String(rawSource)) : null;
          const at = Date.now();
          seq.current += 1;
          setTraffic(prev => [{ id: `${at}-${seq.current}`, at, topic, source }, ...prev].slice(0, MAX_TRAFFIC));
          if (source) setLastSeen(prev => ({ ...prev, [source]: at }));
        } catch { /* keep-alives and comments are not JSON */ }
      };
      es.onerror = () => {
        setBusOpen(false);
        try { es?.close(); } catch { /* already closed */ }
        if (closed) return;
        attempt += 1;
        timer = window.setTimeout(connect, Math.min(20000, 2000 * 2 ** Math.min(attempt, 3)));
      };
    };
    connect();
    return () => {
      closed = true;
      if (timer !== null) window.clearTimeout(timer);
      try { es?.close(); } catch { /* nothing to close */ }
    };
  }, []);

  // Re-evaluate staleness and "recently active" highlights without waiting for a frame.
  useEffect(() => {
    const t = setVisibleInterval(() => setTick(n => n + 1), 2000);
    return () => t();
  }, []);

  const fresh = socketOpen && Date.now() - pushedAt < STALE_MS && Object.keys(pushed).length > 0;
  const transport: Transport = fresh ? 'websocket' : 'probe';
  const statuses = useMemo(() => {
    const merged: Record<string, LiveStatus> = { ...(probe.statuses as Record<string, LiveStatus>) };
    if (fresh) for (const [id, st] of Object.entries(pushed)) merged[id] = st;
    return merged;
  }, [fresh, pushed, probe.statuses]);

  const all = useMemo(() => [...SERVICE_REGISTRY, ...INFRA_SERVICES], []);
  const online = all.filter(s => statuses[s.id] === 'UP').length;

  return {
    statuses, online, total: all.length, transport, socketOpen, busOpen, traffic, lastSeen,
    updatedAt: fresh ? pushedAt : (probe.lastCheck ? probe.lastCheck.getTime() : 0),
    settling: !fresh && probe.checking && !probe.lastCheck,
  };
}
